import {
  CustomIconButton,
  CustomList,
  CustomListItemButton,
  CustomListItemIcon,
  CustomListItemText,
} from '@qler-view/shared_lib/src';
import { useMemo, type FC } from 'react';
import type { Menu } from './menuConfig';
import { useNavigate, matchPath } from 'react-router-dom';
import DownArrow from '@assets/images/sidenav/downArrow.svg';
import UpArrow from '@assets/images/sidenav/upArrow.svg';
import { Box, Collapse } from '@mui/material';
import Selector from '@assets/images/sidenav/menu_select.svg';
import { useFilters } from '../../routers/FilterContext';

interface ExpandableMenuProps {
  menuItem: Menu;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const ExpandableMenu: FC<ExpandableMenuProps> = ({
  menuItem,
  open,
  setOpen,
}) => {
  const navigate = useNavigate();
  const { clearFilters } = useFilters();
  const isPathActive = (paths: string[], pathname: string) => {
    return paths.some((path) =>
      matchPath({ path: `${path}/*`, end: false }, pathname)
    );
  };

  /** Parent menu active */
  const isActive = useMemo(
    () => isPathActive(menuItem.path, location.pathname),
    [menuItem.path, location.pathname]
  );

  /** Submenu active states (Sonar safe) */
  const subMenuItems = useMemo(() => {
    if (!menuItem.subMenu) return [];

    return menuItem.subMenu.map((subMenu) => ({
      ...subMenu,
      isActive: isPathActive(subMenu.path, location.pathname),
    }));
  }, [menuItem.subMenu, location.pathname]);

  return (
    <>
      <CustomListItemButton
        selected={isActive}
        className={`listItem ${isActive ? 'active selected' : ''}`}
        onClick={() => {
          if (menuItem.subMenu) {
            setOpen(!open);
          } else if (menuItem.path) {
            setOpen(false);
            const targetPath = menuItem.path[0];
            const isCurrentPath = isPathActive(
              menuItem.path,
              location.pathname
            );
            if (!isCurrentPath) {
              clearFilters();
            }
            navigate(targetPath);
          }
        }}
      >
        <CustomListItemIcon>
          {menuItem.path && (
            <CustomIconButton
              id={`icon${menuItem.name}`}
              icon={isActive ? <menuItem.iconColor /> : <menuItem.icon />}
              className="sidebar-icon"
            />
          )}

          {!menuItem.path && (
            <CustomIconButton
              id={`icon${menuItem.name}`}
              icon={open ? <menuItem.iconColor /> : <menuItem.icon />}
              className="sidebar-icon"
            />
          )}
        </CustomListItemIcon>

        <CustomListItemText
          primary={menuItem.name}
          className={`${isActive ? 'select-text-color' : ''}`}
        />

        {menuItem.subMenu && (
          <CustomIconButton
            id={`icon${menuItem.name}`}
            icon={open ? <UpArrow /> : <DownArrow />}
            className="sidebar-icon"
          />
        )}

        {isActive && (
          <Box className="image-container">
            <Selector />
          </Box>
        )}
      </CustomListItemButton>
      {menuItem.subMenu && (
        <Collapse in={open || isActive} timeout="auto" unmountOnExit>
          <CustomList component="div" disablePadding>
            {subMenuItems.map((subMenu) => (
              <CustomListItemButton
                key={subMenu.name}
                selected={subMenu.isActive}
                className={`listItem ${subMenu.isActive ? 'active selected' : ''}`}
                onClick={() => {
                  const targetPath = subMenu.path[0];
                  const isCurrentPath = isPathActive(
                    subMenu.path,
                    location.pathname
                  );
                  if (!isCurrentPath) {
                    clearFilters();
                  }
                  navigate(targetPath);
                }}
              >
                <CustomListItemIcon />
                <CustomListItemText
                  primary={subMenu.name}
                  className={`${subMenu.isActive ? 'select-text-color' : ''}`}
                />

                {subMenu.isActive && (
                  <Box className="image-container">
                    <Selector />
                  </Box>
                )}
              </CustomListItemButton>
            ))}
          </CustomList>
        </Collapse>
      )}
    </>
  );
};

export default ExpandableMenu;
